import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import {
  BuildingIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LinkIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
  UserIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SimpleSelect } from '@/components/ui/simple-select'
import { Textarea } from '@/components/ui/textarea'
import {
  focusLabelFor,
  fuzzySearchCompanies,
  INTERVIEW_FOCUSES,
  INTERVIEW_STAGES,
  type InterviewContact,
  type InterviewFocus,
  type InterviewLink,
  type InterviewStage,
  type ScheduledInterview,
  STAGE_META,
  stageLabelFor,
} from '@/lib/dashboard-interviews'
import { cn } from '@/lib/utils'
import { toKey } from '../data/homework-data'
import { weekdayHeaders } from '../data/types'

// ---------------------------------------------------------------------------
// Segmented control (dependency-free, matches the mock's outline toggle)
// ---------------------------------------------------------------------------

function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T
  onChange: (value: T) => void
  options: { value: T; label: string }[]
  ariaLabel: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex w-fit items-center rounded-md border p-0.5"
    >
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-[calc(var(--radius)-6px)] px-3 py-1 text-sm font-medium transition-colors',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

// Single-letter labels, so the key carries the full name to stay unique.
function miniCalendarHeaders(): { key: string; label: string }[] {
  return weekdayHeaders().map((name) => ({
    key: name.toLowerCase(),
    label: name.slice(0, 1),
  }))
}

// ---------------------------------------------------------------------------
// Mini calendar used for both locked ranges and availability days
// ---------------------------------------------------------------------------

function MiniCalendar({
  mode,
  selectedDays,
  onToggleDay,
  month,
  onMonthChange,
}: {
  mode: 'locked' | 'availability'
  selectedDays: string[]
  onToggleDay: (dayKey: string) => void
  month: Date
  onMonthChange: (month: Date) => void
}) {
  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 })
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 })
  const days: Date[] = []
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d)

  const selectedSet = new Set(selectedDays)
  const sorted = [...selectedDays].sort()
  const rangeStart = sorted[0]
  const rangeEnd = sorted[sorted.length - 1]

  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {format(month, 'MMMM yyyy')}
        </span>
        <span className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onMonthChange(addMonths(month, -1))}
            aria-label="Previous month"
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onMonthChange(addMonths(month, 1))}
            aria-label="Next month"
          >
            <ChevronRightIcon />
          </Button>
        </span>
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {miniCalendarHeaders().map((wd) => (
          <span
            key={wd.key}
            className="py-1 text-center text-[10px] font-medium text-muted-foreground uppercase"
          >
            {wd.label}
          </span>
        ))}
        {days.map((day) => {
          const key = toKey(day)
          const inMonth = isSameMonth(day, month)
          const selected = selectedSet.has(key)
          const inRange =
            mode === 'locked' &&
            rangeStart &&
            rangeEnd &&
            key > rangeStart &&
            key < rangeEnd

          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggleDay(key)}
              className={cn(
                'flex h-8 items-center justify-center rounded-md text-xs tabular-nums transition-colors',
                !inMonth && 'text-muted-foreground/50',
                selected
                  ? 'bg-primary font-semibold text-primary-foreground'
                  : inRange
                    ? 'bg-primary/15 text-foreground'
                    : 'hover:bg-accent',
                isToday(day) && !selected && 'ring-1 ring-primary/50',
              )}
              aria-pressed={selected}
              aria-label={format(day, 'EEEE, MMMM d')}
            >
              {format(day, 'd')}
            </button>
          )
        })}
      </div>
      <p className="text-xs text-muted-foreground text-pretty">
        {mode === 'locked'
          ? 'Pick the confirmed day — click a second day to lock a multi-day range (onsites are often more than one day).'
          : 'Click every day you offered as possible availability.'}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Fuzzy company search
// ---------------------------------------------------------------------------

function CompanySearch({
  value,
  onChange,
}: {
  value: string
  onChange: (name: string) => void
}) {
  const [query, setQuery] = useState(value)
  const [open, setOpen] = useState(false)
  const matches = fuzzySearchCompanies(query)

  useEffect(() => {
    setQuery(value)
  }, [value])

  return (
    <div className="relative flex flex-col gap-1.5">
      <Label htmlFor="interview-company">Company</Label>
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id="interview-company"
          placeholder="Search companies..."
          className="pl-9"
          value={query}
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value)
            onChange(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            window.setTimeout(() => setOpen(false), 150)
          }}
        />
      </div>
      {open && matches.length > 0 && (
        <ul
          className="absolute top-full z-20 mt-1 flex w-full flex-col overflow-hidden rounded-lg border bg-popover p-1 shadow-md"
          aria-label="Company suggestions"
        >
          {matches.map((name) => (
            <li key={name}>
              <button
                type="button"
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
                  name === value && 'bg-accent',
                )}
                onClick={() => {
                  setQuery(name)
                  onChange(name)
                  setOpen(false)
                }}
              >
                <BuildingIcon
                  className="size-3.5 text-muted-foreground"
                  aria-hidden="true"
                />
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Form dialog
// ---------------------------------------------------------------------------

const makeId = () =>
  `iv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`

// A row the user added but never typed into — safe to drop silently on save.
const isContactEmpty = (c: InterviewContact) =>
  !c.name.trim() && !c.role.trim() && !c.email.trim() && !c.phone.trim()
const contactNeedsName = (c: InterviewContact) =>
  !c.name.trim() && !isContactEmpty(c)

const isLinkEmpty = (l: InterviewLink) => !l.label.trim() && !l.url.trim()
const linkNeedsUrl = (l: InterviewLink) => !l.url.trim() && !isLinkEmpty(l)

// The server schema requires a full URL; people paste bare domains.
const normalizeUrl = (url: string) => {
  const trimmed = url.trim()
  if (!trimmed || /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

interface InterviewFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Present when editing an existing interview */
  initial?: ScheduledInterview | null
  onSave: (interview: ScheduledInterview) => Promise<void> | void
  isSaving?: boolean
}

export function InterviewFormDialog({
  open,
  onOpenChange,
  initial,
  onSave,
  isSaving = false,
}: InterviewFormDialogProps) {
  const [company, setCompany] = useState('')
  const [roleTitle, setRoleTitle] = useState('')
  const [stage, setStage] = useState<InterviewStage>('phone-screen')
  const [customStageKind, setCustomStageKind] = useState('custom')
  const [customStageLabel, setCustomStageLabel] = useState('')
  const [knownStageLabel, setKnownStageLabel] = useState('')
  const [focuses, setFocuses] = useState<InterviewFocus[]>([])
  const [mode, setMode] = useState<'locked' | 'availability' | 'tbd'>(
    'availability',
  )
  const [days, setDays] = useState<string[]>([])
  const [calendarMonth, setCalendarMonth] = useState(() =>
    startOfMonth(new Date()),
  )
  const [notes, setNotes] = useState('')
  const [contacts, setContacts] = useState<InterviewContact[]>([])
  const [links, setLinks] = useState<InterviewLink[]>([])

  useEffect(() => {
    if (!open) return
    if (initial) {
      const knownStage = STAGE_META[initial.stage]
      const isCustomStage = !knownStage || initial.stage === 'custom'
      setCompany(initial.company)
      setRoleTitle(initial.roleTitle ?? '')
      setStage(isCustomStage ? 'custom' : initial.stage)
      setCustomStageKind(
        isCustomStage
          ? initial.stage === 'custom'
            ? 'custom'
            : initial.stage
          : 'custom',
      )
      setCustomStageLabel(
        isCustomStage
          ? initial.stageLabel?.trim() ||
              (initial.stage === 'custom' ? '' : initial.stage)
          : '',
      )
      setKnownStageLabel(
        isCustomStage
          ? ''
          : initial.stageLabel?.trim() ||
              stageLabelFor(initial.stage, STAGE_META[initial.stage]?.label),
      )
      setFocuses(initial.focuses)
      setMode(initial.scheduling.kind)
      const nextDays =
        initial.scheduling.kind === 'locked'
          ? [initial.scheduling.startDate, initial.scheduling.endDate].filter(
              (v, i, a) => a.indexOf(v) === i,
            )
          : initial.scheduling.kind === 'availability'
            ? initial.scheduling.dates
            : []
      setDays(nextDays)
      // Snap the calendar to the earliest selected day so far-off interviews
      // don't open on the current month with nothing highlighted.
      const firstDay = [...nextDays].sort()[0]
      setCalendarMonth(startOfMonth(firstDay ? parseISO(firstDay) : new Date()))
      setNotes(initial.notes)
      setContacts(initial.contacts)
      setLinks(initial.links)
    } else {
      setCompany('')
      setRoleTitle('')
      setStage('phone-screen')
      setCustomStageKind('custom')
      setCustomStageLabel('')
      setKnownStageLabel(
        stageLabelFor('phone-screen', STAGE_META['phone-screen']?.label),
      )
      setFocuses([])
      setMode('availability')
      setDays([])
      setCalendarMonth(startOfMonth(new Date()))
      setNotes('')
      setContacts([])
      setLinks([])
    }
  }, [open, initial])

  const handleToggleDay = (key: string) => {
    setDays((prev) => {
      if (prev.includes(key)) return prev.filter((d) => d !== key)
      if (mode === 'locked') {
        if (prev.length >= 2) return [key]
        return [...prev, key].sort()
      }
      return [...prev, key].sort()
    })
  }

  const resolvedStage = stage === 'custom' ? customStageKind || 'custom' : stage
  const resolvedStageLabel =
    stage === 'custom'
      ? customStageLabel.trim()
      : knownStageLabel.trim() || stageLabelFor(stage, STAGE_META[stage]?.label)
  const canSave =
    company.trim().length > 0 &&
    (mode === 'tbd' || days.length > 0) &&
    (stage !== 'custom' || customStageLabel.trim().length > 0) &&
    !contacts.some(contactNeedsName) &&
    !links.some(linkNeedsUrl)

  const handleSave = async () => {
    const sorted = [...days].sort()
    const scheduling =
      mode === 'locked'
        ? {
            kind: 'locked' as const,
            startDate: sorted[0],
            endDate: sorted[sorted.length - 1],
          }
        : mode === 'availability'
          ? { kind: 'availability' as const, dates: sorted }
          : { kind: 'tbd' as const }

    try {
      await onSave({
        id: initial?.id ?? makeId(),
        applicationId: initial?.applicationId,
        applicationStageId: initial?.applicationStageId,
        company: company.trim(),
        roleTitle: roleTitle.trim(),
        stage: resolvedStage,
        stageLabel: resolvedStageLabel,
        focuses,
        scheduling,
        notes,
        contacts: contacts.filter((c) => !isContactEmpty(c)),
        links: links
          .filter((l) => !isLinkEmpty(l))
          .map((l) => ({ ...l, url: normalizeUrl(l.url) })),
        noteEntries: initial?.noteEntries ?? [],
      })
      onOpenChange(false)
    } catch {
      // Keep the dialog open so the user can retry after a failed save.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85svh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {initial ? 'Edit interview' : 'Add an interview'}
          </DialogTitle>
          <DialogDescription>
            Everything here is shared with your coach so prep can adjust around
            it.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <CompanySearch value={company} onChange={setCompany} />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="interview-role">Role (optional)</Label>
              <Input
                id="interview-role"
                placeholder="e.g. Senior SWE, Staff Frontend"
                value={roleTitle}
                onChange={(e) => setRoleTitle(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Interview type</Label>
              <SimpleSelect
                value={stage}
                onChange={(v) => {
                  setStage(v as InterviewStage)
                  if (v !== 'custom') {
                    setCustomStageKind('custom')
                    setCustomStageLabel('')
                    setKnownStageLabel(stageLabelFor(v, STAGE_META[v]?.label))
                  } else if (!customStageKind) {
                    setCustomStageKind('custom')
                  }
                }}
                width="full"
                options={INTERVIEW_STAGES.map((s) => ({
                  value: s,
                  label: stageLabelFor(s, STAGE_META[s]?.label),
                }))}
              />
              {stage === 'custom' && (
                <Input
                  placeholder="Describe the round"
                  aria-label="Custom interview type"
                  value={customStageLabel}
                  onChange={(e) => setCustomStageLabel(e.target.value)}
                />
              )}
              {STAGE_META[stage]?.note && (
                <p className="text-xs text-muted-foreground">
                  {STAGE_META[stage].note}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Scheduling</Label>
              <Segmented
                ariaLabel="Scheduling mode"
                value={mode}
                onChange={(next) => {
                  setMode(next)
                  setDays([])
                }}
                options={[
                  { value: 'availability', label: 'Availability given' },
                  { value: 'locked', label: 'Locked in' },
                  { value: 'tbd', label: 'Date TBD' },
                ]}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Focus areas</Label>
            <div className="flex flex-wrap gap-1.5">
              {INTERVIEW_FOCUSES.map((f) => {
                const active = focuses.includes(f)
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() =>
                      setFocuses((prev) =>
                        active ? prev.filter((x) => x !== f) : [...prev, f],
                      )
                    }
                    aria-pressed={active}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    {focusLabelFor(f)}
                  </button>
                )
              })}
            </div>
          </div>

          {mode === 'tbd' ? (
            <p className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground text-pretty">
              No dates yet — the interview is confirmed but not scheduled. Come
              back and add dates once the recruiter locks a time.
            </p>
          ) : (
            <MiniCalendar
              mode={mode}
              selectedDays={days}
              onToggleDay={handleToggleDay}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
            />
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="interview-notes">Notes for your coach</Label>
            <Textarea
              id="interview-notes"
              placeholder="Anything your coach should know — recruiter said the onsite has 2 coding rounds, they hinted at a system design bar-raiser, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Contacts */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <Label className="flex items-center gap-1.5">
                <UserIcon className="size-3.5" aria-hidden="true" />
                People
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setContacts((prev) => [
                    ...prev,
                    { id: makeId(), name: '', role: '', email: '', phone: '' },
                  ])
                }
              >
                <PlusIcon />
                Add person
              </Button>
            </div>
            {contacts.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Recruiter, hiring manager, or anyone else you know — only a name
                is needed; add email or phone if you have them.
              </p>
            )}
            {contacts.map((contact, idx) => (
              <div
                key={contact.id}
                className="grid gap-2 rounded-lg border p-3 sm:grid-cols-2"
              >
                <Input
                  placeholder="Name"
                  aria-label={`Person ${idx + 1} name`}
                  aria-invalid={contactNeedsName(contact)}
                  value={contact.name}
                  onChange={(e) =>
                    setContacts((prev) =>
                      prev.map((c) =>
                        c.id === contact.id
                          ? { ...c, name: e.target.value }
                          : c,
                      ),
                    )
                  }
                />
                <Input
                  placeholder="Role (e.g. Recruiter)"
                  aria-label={`Person ${idx + 1} role`}
                  value={contact.role}
                  onChange={(e) =>
                    setContacts((prev) =>
                      prev.map((c) =>
                        c.id === contact.id
                          ? { ...c, role: e.target.value }
                          : c,
                      ),
                    )
                  }
                />
                <Input
                  type="email"
                  placeholder="Email"
                  aria-label={`Person ${idx + 1} email`}
                  value={contact.email}
                  onChange={(e) =>
                    setContacts((prev) =>
                      prev.map((c) =>
                        c.id === contact.id
                          ? { ...c, email: e.target.value }
                          : c,
                      ),
                    )
                  }
                />
                <div className="flex items-center gap-2">
                  <Input
                    type="tel"
                    placeholder="Phone"
                    aria-label={`Person ${idx + 1} phone`}
                    value={contact.phone}
                    onChange={(e) =>
                      setContacts((prev) =>
                        prev.map((c) =>
                          c.id === contact.id
                            ? { ...c, phone: e.target.value }
                            : c,
                        ),
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove person ${idx + 1}`}
                    onClick={() =>
                      setContacts((prev) =>
                        prev.filter((c) => c.id !== contact.id),
                      )
                    }
                  >
                    <Trash2Icon />
                  </Button>
                </div>
                {contactNeedsName(contact) && (
                  <p className="text-xs text-destructive sm:col-span-2">
                    Add a name to save this person — everything else is
                    optional.
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Links */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <Label className="flex items-center gap-1.5">
                <LinkIcon className="size-3.5" aria-hidden="true" />
                Important links
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setLinks((prev) => [
                    ...prev,
                    { id: makeId(), label: '', url: '' },
                  ])
                }
              >
                <PlusIcon />
                Add link
              </Button>
            </div>
            {links.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Emails, scheduling pages, prep docs the recruiter sent, etc.
              </p>
            )}
            {links.map((link, idx) => (
              <div key={link.id} className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Label (e.g. Recruiter email thread)"
                    aria-label={`Link ${idx + 1} label`}
                    className="max-w-56"
                    value={link.label}
                    onChange={(e) =>
                      setLinks((prev) =>
                        prev.map((l) =>
                          l.id === link.id
                            ? { ...l, label: e.target.value }
                            : l,
                        ),
                      )
                    }
                  />
                  <Input
                    type="url"
                    placeholder="https://..."
                    aria-label={`Link ${idx + 1} URL`}
                    aria-invalid={linkNeedsUrl(link)}
                    value={link.url}
                    onChange={(e) =>
                      setLinks((prev) =>
                        prev.map((l) =>
                          l.id === link.id ? { ...l, url: e.target.value } : l,
                        ),
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove link ${idx + 1}`}
                    onClick={() =>
                      setLinks((prev) => prev.filter((l) => l.id !== link.id))
                    }
                  >
                    <Trash2Icon />
                  </Button>
                </div>
                {linkNeedsUrl(link) && (
                  <p className="text-xs text-destructive">
                    Add a URL to save this link.
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave || isSaving}>
            {isSaving
              ? 'Saving...'
              : initial
                ? 'Save changes'
                : 'Add interview'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
